import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { faArchive, faCaretDown, faCaretUp, faSort, faXmark } from '@fortawesome/free-solid-svg-icons';
import { Color, LegendPosition, ScaleType } from '@swimlane/ngx-charts';

import { PlatformService } from '../../../../core/services/platform/platform.service';
import { UtilService } from '../../../../core/services/util/util.service';
import { PuchaseAnalysisService } from '../../services/api/puchase-analysis.service';
import { PlatformGroupService } from '../../../../core/services/platform-group/platform-group.service';
import { CategoryService } from '../../../../core/services/category/category.service';

//=========================================================
// Interfaces
//=========================================================

interface ChartDataset {
  name: string;
  series: Series[];
}

interface Series {
  name: string;
  value: number;
}

interface GroupSummary {
  platform_group_id: string;
  total_amount: number;
  total_qty: number;
}

interface TableDataset {
  sku: string;
  name: string;
  stock: number;
  groups: GroupSummary[];
  total_qty: number;
  total_amount: number;
  avg_qty_per_day: number;
  avg_amount_per_day: number;
  sale_in_day: number;
  tags: string[];
}

interface ProductInfo {
  sku: string;
  name: string;
  in_stock?: number;
  tags: string[];
}

interface ProductSummary {
  platform_group_id: string;
  sku: string;
  total_qty: number;
  total_amount: number;
}

@Component({
  selector: 'app-purchase-analysis-product-overview',
  templateUrl: './purchase-analysis-product-overview.component.html',
  styleUrl: './purchase-analysis-product-overview.component.css'
})
export class PurchaseAnalysisProductOverviewComponent implements OnInit {
  //=========================================================
  // Icons and Constants
  //=========================================================
  readonly faCaretDown = faCaretDown;
  readonly faCaretUp = faCaretUp;
  readonly faSort = faSort;
  readonly faXmark = faXmark;
  readonly faArchive = faArchive;
  readonly legendPosition = LegendPosition.Right;

  //=========================================================
  // Platform and Channel Data
  //=========================================================
  channels: any[] = [];
  platformGroup: any[] = [];
  platformGroupFiltered: any[] = [];
  selectedPlatfromGroup: string[] = [];
  readonly ALL_GROUP_MARKER = '__all__';
  readonly chartColors = ['#647c8a', '#3f51b5', '#2196f3', '#00b862', '#afdf0a', '#a7b61a', '#f3e562', '#ff9800', '#ff5722', '#ff9907'];
  hiddenGroups = new Set<string>();
  chartScheme: Color = { name: 'platformGroup', selectable: true, group: ScaleType.Ordinal, domain: [] };


  //=========================================================
  // filter tags
  //=========================================================
  selectedTag: string[] = [];
  readonly ALL_TAG_MARKER = '__all_tag__';

  //=========================================================
  // Product Data
  //=========================================================
  products: ProductInfo[] = [];
  keyword = '';
  productsFilter: ProductInfo[] = [];
  showProductFilter = false;
  productSelected: ProductInfo[] = [];
  productSummary: ProductSummary[] = [];
  productFiltered: Array<{ sku: string; total_qty: number; total_amount: number, avg_qty_per_day: number, avg_amount_per_day: number }> = [];
  categories: Array<{ id: string; name: string; }> = this.categoryService?.categories || []

  //=========================================================
  // Table Data
  //=========================================================
  tableDatasets: TableDataset[] = [];
  displayData: 'total_qty' | 'total_amount' = 'total_qty';

  //=========================================================
  // Pagination
  //=========================================================
  countDataSource = 0;
  pageSize = 10;
  pageIndex = 0;
  limit = 10;
  offset = 0;

  //=========================================================
  // Date Selection
  //=========================================================
  selectedDate: { type: string, value: string } = { type: 'daypass', value: '30' };

  //=========================================================
  // Form and UI State
  //=========================================================
  form: FormGroup;
  loading = false;

  //=========================================================
  // Chart and Performance Data
  //=========================================================
  selectedRow = '';
  performanceRows: Array<{
    sku: string;
    platform_group_id: string;
    date: string;
    total_amount: number;
    total_qty: number;
  }> = [];
  performanceDatasets: ChartDataset[] = [];
  chartLoaded = false;
  chartDataset: ChartDataset[] = []
  performanceChartLabel = '';
  private chartStartDate = '';
  private chartEndDate = '';

  //=========================================================
  // Sort Options
  //=========================================================
  selectedSortFiled = '';
  selectedSortPlatfromGroup = '';
  sortBy: 'max' | 'min' = 'max';

  //=========================================================
  // product Tags
  //=========================================================

  tags: string[] = []

  //=========================================================
  // Constructor and Lifecycle Methods
  //=========================================================
  constructor(
    private purchaseAnalysisService: PuchaseAnalysisService,
    private utilService: UtilService,
    private platformService: PlatformService,
    private fb: FormBuilder,
    private platformGroupService: PlatformGroupService,
    private categoryService: CategoryService
  ) {
    this.form = this.fb.group({
      date: [this.computeDateRange(this.selectedDate)],
      category_id: ['']
    });
  }

  handleOptionSelected(selectedDate: { type: string, value: string }): void {
    this.selectedDate = selectedDate;
    this.form.get('date')?.setValue(this.computeDateRange(selectedDate));
  }

  private computeDateRange(option: { type: string, value: string }): string {
    const dayjs = this.utilService.dayjs;
    if (option.type === 'date-range') {
      const [s, e] = option.value.split(',');
      return `${dayjs(s).format('YYYY-MM-DD')},${dayjs(e).format('YYYY-MM-DD')}`;
    }
    if (option.value === '0') {
      const d = dayjs().format('YYYY-MM-DD');
      return `${d},${d}`;
    }
    if (option.value === '1') {
      const d = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
      return `${d},${d}`;
    }
    if (option.value === 'month') {
      const start = dayjs().startOf('month').format('YYYY-MM-DD');
      const end = dayjs().format('YYYY-MM-DD');
      return `${start},${end}`;
    }
    const start = dayjs().subtract(parseInt(option.value), 'day').format('YYYY-MM-DD');
    const end = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
    return `${start},${end}`;
  }

  ngOnInit(): void {
    this.getProductTag();
    this.initializePlatformData();
    this.getDataReport();
  }

  getProductTag() {
    this.tags = []
    this.purchaseAnalysisService.getProductTag({}).then((res: any) => {
      const tags: Array<{ name: string }> = res?.data || []
      this.tags = tags.map(item => item.name)
    })
  }

  private initializePlatformData(): void {
    this.platformGroup = this.platformGroupService.platformGroup;
    const groupIds = this.platformGroup.map(item => item.id);
    this.channels = this.platformService.channelsAll.filter(item =>
      groupIds.includes(item.platform_group_id)
    );
    this.platformGroupFiltered = this.platformGroup;
  }

  //=========================================================
  // Data Fetching Methods
  //=========================================================
  /**
   * หลักการทำงานหลักในการดึงข้อมูลรายงาน
   */
  getDataReport(): void {
    this.loading = true;
    const formValues = this.form.getRawValue();
    const filter = this.buildReportFilter(formValues);

    // รีเซ็ตข้อมูลก่อนเรียก API
    this.resetReportData();

    this.purchaseAnalysisService.getProductSummaryReportByGroup(filter)
      .then(async (res: any) => {
        if (res.success) {
          this.processSuccessResponse(res.data);

          // ถ้ามีข้อมูลสินค้า ให้ดึงรายละเอียดสินค้า
          const uniqueSku = [...new Set(this.productFiltered.map(e => e.sku))];
          if (uniqueSku.length > 0) {
            await this.findAllProduct({ sku: uniqueSku.join(), filed: 'sku,name' });
          }
        }
        this.setFormattingData();
      })
      .finally(() => {
        this.loading = false;
      });
  }

  /**
   * ดึงข้อมูลสรุปรายวันของสินค้า
   */
  getAllSummaryReport(sku: string): void {
    this.chartLoaded = false;
    const dateRange = this.computeDateRange(this.selectedDate);
    const [startDate, endDate] = dateRange.split(',');
    this.chartStartDate = startDate;
    this.chartEndDate = endDate;
    this.performanceChartLabel = this.buildPerformanceChartLabel(startDate, endDate);

    const filters: any = {
      sku,
      date: dateRange,
      platform_group_id: this.selectedPlatfromGroup.length > 0
        ? this.selectedPlatfromGroup.toString()
        : this.platformGroup.map(item => item.id).toString()
    };
    this.purchaseAnalysisService.getProductDailyReportByGroup(filters)
      .then((res: any) => {
        if (res.success) {
          this.performanceRows = res.data;
          this.setFormattingPerformanceChart();
        } else {
          this.performanceRows = [];
        }
      })
      .finally(() => {
        this.chartLoaded = true;
      });
  }

  private buildPerformanceChartLabel(startDate: string, endDate: string): string {
    const dayjs = this.utilService.dayjs;
    const start = dayjs(startDate).format('D MMM YYYY');
    const end = dayjs(endDate).format('D MMM YYYY');
    return start === end ? `ยอดขาย ${start}` : `ยอดขาย ${start} - ${end}`;
  }

  /**
   * ค้นหาสินค้าตาม keyword
   */
  async searchProduct(keyword: string): Promise<ProductInfo[]> {
    try {
      const response: any = await this.purchaseAnalysisService.searchProduct({
        keyword,
        limit: 20
      });

      return response.success ? response.data : [];
    } catch (error) {
      console.error('Error searching products:', error);
      return [];
    }
  }

  /**
   * ดึงข้อมูลสินค้าทั้งหมดตาม filter
   */
  async findAllProduct(filter: any): Promise<void> {
    try {
      const response: any = await this.purchaseAnalysisService.getAllProduct(filter);
      this.products = response.success ? response.data : [];
    } catch (error) {
      console.error('Error finding products:', error);
      this.products = [];
    }
  }

  //=========================================================
  // Filter and Parameter Building Methods
  //=========================================================
  /**
   * สร้าง filter สำหรับดึงข้อมูลรายงาน
   */
  private buildReportFilter(formValues: any): any {
    const filter: any = {
      limit: this.limit,
      offset: this.offset,
      sort_field: this.displayData,
      sort_by: this.sortBy
    };

    if (formValues.date) {
      filter.date = formValues.date;
    }

    if (this.selectedPlatfromGroup?.length > 0) {
      filter.platform_group_id = this.selectedPlatfromGroup.join();
      this.platformGroupFiltered = this.platformGroup.filter(group =>
        this.selectedPlatfromGroup.includes(group.id)
      );
    } else {
      filter.platform_group_id = this.platformGroup.map(group => group.id).toString();
      this.platformGroupFiltered = this.platformGroup;
    }

    if (this.productSelected.length > 0) {
      filter.sku = this.productSelected.map(e => e.sku).join();
    }

    if (this.selectedSortFiled) {
      filter.sort_field = this.selectedSortFiled;
    }

    if (this.selectedSortPlatfromGroup) {
      filter.sort_platform_group_id = this.selectedSortPlatfromGroup;
    }

    if (this.form?.getRawValue()?.category_id) {
      filter.category_id = this.form?.getRawValue()?.category_id
    }

    if (this.selectedTag?.length > 0) {
      filter.tags = this.selectedTag.join()
    }

    return filter;
  }

  /**
   * รีเซ็ตข้อมูลก่อนดึงข้อมูลใหม่
   */
  private resetReportData(): void {
    this.productSummary = [];
    this.productFiltered = [];
    this.countDataSource = 0;
    this.selectedRow = '';
  }

  /**
   * ประมวลผลข้อมูลที่ได้จาก API
   */
  private processSuccessResponse(data: any): void {
    this.productFiltered = data.products;
    this.productSummary = data.summary;
    this.countDataSource = data.count;
  }

  //=========================================================
  // Data Formatting Methods
  //=========================================================
  /**
   * จัดรูปแบบข้อมูลสำหรับแสดงในตาราง
   */
  readonly TOTAL_ROW_NAME = 'รวม';

  setFormattingData(): void {
    this.rebuildChartScheme();
    this.chartDataset = this.formatGraphData(this.productSummary);

    const productRows: TableDataset[] = this.productFiltered.map(product => {
      const productInfo = this.products.find(p => p.sku === product.sku);
      const stock = productInfo?.in_stock || 0;
      const groups: GroupSummary[] = this.platformGroupFiltered.map(group => {
        const summary = this.productSummary.find(e => e.platform_group_id === group.id && e.sku === product.sku);
        return {
          platform_group_id: group.id,
          total_amount: summary?.total_amount || 0,
          total_qty: summary?.total_qty || 0
        };
      });
      return {
        sku: product.sku,
        stock,
        name: productInfo?.name || '',
        groups,
        total_qty: product.total_qty,
        total_amount: product.total_amount,
        avg_qty_per_day: product.avg_qty_per_day,
        avg_amount_per_day: product.avg_amount_per_day,
        sale_in_day: product.avg_qty_per_day > 0 ? Math.round(stock / product.avg_qty_per_day) : 0,
        tags: productInfo?.tags || []
      };
    });

    this.tableDatasets = productRows.length > 0
      ? [...productRows, this.buildTotalRow(productRows)]
      : [];
  }

  private buildTotalRow(rows: TableDataset[]): TableDataset {
    const groups: GroupSummary[] = this.platformGroupFiltered.map(group => ({
      platform_group_id: group.id,
      total_amount: rows.reduce((sum, r) => sum + (r.groups.find(g => g.platform_group_id === group.id)?.total_amount || 0), 0),
      total_qty: rows.reduce((sum, r) => sum + (r.groups.find(g => g.platform_group_id === group.id)?.total_qty || 0), 0)
    }));
    return {
      sku: '',
      name: this.TOTAL_ROW_NAME,
      stock: rows.reduce((sum, r) => sum + r.stock, 0),
      groups,
      total_qty: rows.reduce((sum, r) => sum + r.total_qty, 0),
      total_amount: rows.reduce((sum, r) => sum + r.total_amount, 0),
      avg_qty_per_day: 0,
      avg_amount_per_day: 0,
      sale_in_day: 0,
      tags: []
    };
  }

  readonly chartTopN = 5;

  formatGraphData(rawData: any[]): ChartDataset[] {
    const groups = this.chartGroups;
    const sortedSkus = [...this.productFiltered]
      .sort((a, b) => (b[this.displayData] || 0) - (a[this.displayData] || 0))
      .slice(0, this.chartTopN)
      .map(p => p.sku);

    return sortedSkus.map(sku => {
      const productName = this.products.find(p => p.sku === sku)?.name || sku;
      const series: Series[] = groups.map(group => {
        const summary = rawData.find(item => item.sku === sku && item.platform_group_id === group.id);
        const isHidden = this.hiddenGroups.has(group.id);
        return {
          name: group.name,
          value: isHidden ? 0 : (summary?.[this.displayData] || 0)
        };
      });
      return { name: productName, series };
    });
  }

  get chartGroups(): any[] {
    return this.platformGroupFiltered.length > 0 ? this.platformGroupFiltered : this.platformGroup;
  }

  private rebuildChartScheme(): void {
    this.chartScheme = {
      name: 'platformGroup',
      selectable: true,
      group: ScaleType.Ordinal,
      domain: this.chartGroups.map((g, i) => g.color || this.chartColors[i % this.chartColors.length])
    };
  }

  groupColor(index: number): string {
    const group = this.chartGroups[index];
    return group?.color || this.chartColors[index % this.chartColors.length];
  }

  groupShopsLabel(groupId: string): string {
    const shops = this.channels
      .filter(c => c.platform_group_id === groupId)
      .map(c => c.platform_name || c.shop_ref);
    if (shops.length === 0) return 'ไม่มีร้านค้าในกลุ่มนี้';
    return `${shops.length} ร้าน:\n• ${shops.join('\n• ')}`;
  }

  togglePlatformGroupVisibility(groupId: string): void {
    if (this.hiddenGroups.has(groupId)) {
      this.hiddenGroups.delete(groupId);
    } else {
      this.hiddenGroups.add(groupId);
    }
    this.chartDataset = this.formatGraphData(this.productSummary);
  }

  /**
   * จัดรูปแบบข้อมูลสำหรับแสดงในกราฟ
   */
  setFormattingPerformanceChart(): void {
    this.performanceDatasets = [];

    for (const group of this.platformGroup) {
      const groupChannelData = this.performanceRows.filter(e => e.platform_group_id === group.id);
      const performanceByDate = this.groupPerformanceDataByDate(groupChannelData);
      const series: Series[] = this.generateTimeSeriesData(performanceByDate, this.chartStartDate, this.chartEndDate);

      this.performanceDatasets.push({
        name: group.name,
        series
      });
    }
  }


  /**
   * จัดกลุ่มข้อมูลประสิทธิภาพตามวันที่
   */
  private groupPerformanceDataByDate(channelData: any[]): Record<string, any> {
    return channelData.reduce((acc, item) => ({
      ...acc,
      [item.date]: item
    }), {});
  }

  /**
   * สร้างข้อมูลอนุกรมเวลาสำหรับกราฟ
   */
  private generateTimeSeriesData(performanceByDate: Record<string, any>, startDate: string, endDate: string): Series[] {
    const series: Series[] = [];
    const start = this.utilService.dayjs(startDate);
    const totalDays = this.utilService.dayjs(endDate).diff(start, 'day');

    for (let index = 0; index <= totalDays; index++) {
      const date = start.add(index, 'day').format('YYYY-MM-DD');
      const value = this.displayData === 'total_qty'
        ? (performanceByDate[date]?.total_qty || 0)
        : (performanceByDate[date]?.total_amount || 0);

      series.push({ name: date, value });
    }

    return series;
  }

  //=========================================================
  // Event Handlers - Platform Group
  //=========================================================
  removePlatfromGroupSelected(group: any): void {
    this.selectedPlatfromGroup = this.selectedPlatfromGroup.filter((id: any) => id !== group.id);
  }

  removePlatfromGroupSelectedAll(): void {
    this.selectedPlatfromGroup = [];
  }

  displayPlatfromGroup(): any[] {
    return this.platformGroup.filter(g => this.selectedPlatfromGroup.includes(g.id));
  }

  get displayedPlatformGroupSelection(): string[] {
    if (this.platformGroup.length > 0 && this.selectedPlatfromGroup.length === this.platformGroup.length) {
      return [this.ALL_GROUP_MARKER, ...this.selectedPlatfromGroup];
    }
    return this.selectedPlatfromGroup;
  }

  onPlatformGroupSelectionChange(values: any[]): void {
    const list = Array.isArray(values) ? values : [];
    const wasAllSelected = this.platformGroup.length > 0
      && this.selectedPlatfromGroup.length === this.platformGroup.length;
    const newHasMarker = list.includes(this.ALL_GROUP_MARKER);

    if (newHasMarker !== wasAllSelected) {
      this.selectedPlatfromGroup = newHasMarker
        ? this.platformGroup.map(g => g.id)
        : [];
    } else {
      this.selectedPlatfromGroup = list.filter((v: any) => v !== this.ALL_GROUP_MARKER);
    }
  }

  get displayedTagSelection(): string[] {
    if (this.tags.length > 0 && this.selectedTag.length === this.tags.length) {
      return [this.ALL_TAG_MARKER, ...this.selectedTag];
    }
    return this.selectedTag;
  }

  onTagSelectionChange(values: any[]): void {
    const list = Array.isArray(values) ? values : [];
    const wasAllSelected = this.tags.length > 0
      && this.selectedTag.length === this.tags.length;
    const newHasMarker = list.includes(this.ALL_TAG_MARKER);

    if (newHasMarker !== wasAllSelected) {
      this.selectedTag = newHasMarker
        ? [...this.tags]
        : [];
    } else {
      this.selectedTag = list.filter((v: any) => v !== this.ALL_TAG_MARKER);
    }
  }

  //=========================================================
  // Event Handlers - Product Selection
  //=========================================================
  /**
   * ค้นหาสินค้าเมื่อกรอกคีย์เวิร์ด
   */
  async productSearch(): Promise<void> {
    if (this.keyword.length > 2) {
      const selectedSkus = this.productSelected.map(e => e.sku);
      const searchResults = await this.searchProduct(this.keyword);

      this.productsFilter = searchResults.filter(product =>
        !selectedSkus.includes(product.sku)
      );

      this.showProductFilter = this.productsFilter.length > 0;
    } else {
      this.showProductFilter = false;
    }
  }

  /**
   * เลือกสินค้า
   */
  selectProduct(product: any): void {
    this.productSelected.push(product);
    this.productsFilter = this.productsFilter.filter(e => e.sku !== product.sku);
    if (this.productsFilter.length === 0) {
      this.showProductFilter = false;
    }
  }

  /**
   * ลบสินค้าที่เลือก
   */
  removeProductSelected(product: any): void {
    this.productSelected = this.productSelected.filter(p => p.sku !== product.sku);
  }

  /**
   * ลบสินค้าที่เลือกทั้งหมด
   */
  removeProductSelectedAll(): void {
    this.productSelected = [];
  }

  /**
   * ล้างการค้นหา
   */
  clearSearch(): void {
    this.keyword = '';
    this.showProductFilter = false;
  }

  //=========================================================
  // Event Handlers - Table and Display
  //=========================================================
  /**
   * เปลี่ยนการแสดงผลข้อมูล (จำนวนชิ้น/มูลค่า)
   */
  displayDataOnchange(event: any): void {
    this.displayData = event.value;
    this.getDataReport();
  }

  /**
   * เมื่อเลือกแถวในตาราง
   */
  selectedRowOnChange(row: any): void {
    if (this.selectedRow === row.sku) {
      this.selectedRow = '';
    } else {
      this.selectedRow = row.sku;
      this.getAllSummaryReport(this.selectedRow);
    }
  }

  /**
   * เรียงลำดับข้อมูล
   */
  sortData(sortFiled: string): void {
    if (this.selectedSortFiled !== sortFiled) {
      this.selectedSortFiled = sortFiled;
      this.sortBy = 'max';
    } else {
      if (this.selectedSortPlatfromGroup) {
        this.selectedSortPlatfromGroup = '';
        this.sortBy = 'max';
      } else {
        this.sortBy = this.sortBy === 'max' ? 'min' : 'max';
      }
    }
    this.getDataReport();
  }

  sortByPlatformGroup(sortFiled: string): void {
    if (this.selectedSortPlatfromGroup !== sortFiled) {
      this.selectedSortPlatfromGroup = sortFiled;
      this.sortBy = 'max';
    } else {
      this.sortBy = this.sortBy === 'max' ? 'min' : 'max';
    }
    this.getDataReport();
  }

  //=========================================================
  // Event Handlers - Form and Pagination
  //=========================================================
  /**
   * ส่งฟอร์ม
   */
  submitForm(): void {
    this.offset = 0;
    this.pageIndex = 0;
    this.selectedSortFiled = '';
    this.selectedSortPlatfromGroup = '';
    this.sortBy = 'max';
    this.getDataReport();
  }

  /**
   * Callback เมื่อเปลี่ยนหน้า
   */
  callBackPageChange = (data: any): void => {
    let shouldRefresh = false;

    if (this.offset !== data.offset || this.limit !== data.limit) {
      this.offset = data.offset;
      this.limit = data.limit;
      shouldRefresh = true;
    }

    if (data.pageIndex !== undefined) {
      this.pageIndex = data.pageIndex;
    }

    if (data.limit !== undefined) {
      this.pageSize = data.limit;
    }

    if (shouldRefresh) {
      this.getDataReport();
    }
  }

  tagIncludes(tag: string, sku: string) {
    const tags = this.products.find(item => item.sku === sku)?.tags || []
    return tags.includes(tag)
  }

  async addTag(sku: string, tag: string) {
    const product: any = await this.purchaseAnalysisService.getAllProduct({ sku: sku })
    if (product.success) {
      if (product?.data?.length) {
        const productDetial = product.data[0]
        const tags: string[] = productDetial?.tags || []
        if (tags.includes(tag)) {
          const result: any = await this.purchaseAnalysisService.removeProductTag({ sku, tag })
          const index = this.products.findIndex(item => item.sku === result.data.sku)
          this.products[index] = result.data
        } else {
          const result: any = await this.purchaseAnalysisService.addProductTag({ sku, tag })
          const index = this.products.findIndex(item => item.sku === result.data.sku)
          this.products[index] = result.data
        }
      }
    }
  }
}